import { Suspense, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Route, Switch, useRouteMatch } from 'react-router-dom';

import { Layout } from '@webapp/account/src/components/layout';
import * as LayoutRes from '@webapp/account/src/resources/layout';
import { getProfile, getQuotas } from '@webapp/account/src/resources/profile/async';
import {
    selectAnyLimitLow,
    selectLicenseInfo,
    selectProfile,
    selectProfileAuthSuccess,
} from '@webapp/account/src/resources/profile/selectors';
import { AccountRoutes } from '@webapp/account/src/resources/routes';

import { DevIndicator } from '@webapp/common/components/dev-indicator';
import { ErrorBoundary } from '@webapp/common/components/error-boundary';
import { Loader } from '@webapp/common/components/loader';
import { MetrikaHits } from '@webapp/common/components/metrika-hits';
import { useActions } from '@webapp/common/hooks/use-actions';
import { useDeepSelector } from '@webapp/common/hooks/use-deep-selector';
import { useDevMode } from '@webapp/common/hooks/use-dev-mode';
import { lazy } from '@webapp/common/lib/lazy';
import { GOALS, reachGoal } from '@webapp/common/lib/metrics';
import { LocalStorage, LocalStorageKeys } from '@webapp/common/lib/storage';

import { Account } from './account';

const Auth = lazy(() => import('@webapp/account/src/pages/auth/auth'));
const AuthDirectLogin = lazy(() => import('@webapp/account/src/pages/auth/auth-direct-login'));
const NotFoundPage = lazy(() => import('@webapp/common/components/not-found-page'));
const Preview = lazy(() => import('@webapp/account/src/components/preview'));
const Unsubscribe = lazy(() => import('@webapp/account/src/pages/unsubscribe'));

const SizeExceed: FC = () => (
    <>
        <p>
            {`Место на диске закончилось. Опросы с типом вопроса "Загрузка файла" были отключены автоматически, сбор ответов остановлен.`}
        </p>
        <p>
            {
                'Для повторного запуска опросов с типом вопроса "Загрузка файла" расширьте место при помощи дополнительной лицензии или удалите старые файлы в опросах.'
            }
        </p>
    </>
);

const LimitsLow: FC = () => (
    <p>
        <a href='/account/stats'>{`У одно из ваших опросов`}</a>
        {` израсходовано более 80% лимитов, скоро сбор ответов может быть остановлен.`}
    </p>
);

export const App: FC = () => {
    const authSuccess = useSelector(selectProfileAuthSuccess);
    const { id: profileId, restricted } = useSelector(selectProfile);
    const auth = useRouteMatch(AccountRoutes.auth.root);
    const ttDev = useDevMode();
    const toggleDevMode = useActions(LayoutRes.Actions.setDevMode);
    const getProfileRequest = useActions(getProfile);
    const getQuotasRequest = useActions(getQuotas);
    const { isSizeExceeded, withActiveLicense } = useDeepSelector(selectLicenseInfo);
    const lowLimit = useDeepSelector(selectAnyLimitLow);

    const notice = useMemo(() => {
        if (lowLimit) {
            return <LimitsLow />;
        }

        if (!withActiveLicense) {
            return null;
        }

        if (isSizeExceeded) {
            return <SizeExceed />;
        }

        return null;
    }, [isSizeExceeded, lowLimit, withActiveLicense]);

    const errorData = useMemo(
        () => ({
            profileId,
        }),
        [profileId],
    );

    useEffect(() => {
        toggleDevMode(ttDev);
    }, [toggleDevMode, ttDev]);

    useEffect(() => {
        if (!auth) {
            getProfileRequest();
        }
    }, [auth, getProfileRequest]);

    useEffect(() => {
        if (authSuccess && !restricted) {
            getQuotasRequest();
        }
    }, [authSuccess, restricted, getQuotasRequest]);

    // Регистрация: бэкенд вернул успех на /account/auth/confirm-email, но user_id
    // появляется только после загрузки профиля. Флаг ставит страница подтверждения.
    useEffect(() => {
        if (!profileId || !LocalStorage.get(LocalStorageKeys.YM_REGISTRATION_CONFIRMED)) {
            return;
        }

        LocalStorage.remove(LocalStorageKeys.YM_REGISTRATION_CONFIRMED);
        reachGoal(GOALS.registrationConfirmed, { user_id: profileId });
    }, [profileId]);

    // TODO untangle nested loaders
    return (
        <ErrorBoundary data={errorData}>
            <MetrikaHits />
            <Suspense fallback={<Loader />}>
                <Switch>
                    <Route component={AuthDirectLogin} path={AccountRoutes.authDirect.link} />
                    <Route component={Auth} path={AccountRoutes.auth.link} />
                    <Route component={Unsubscribe} path={AccountRoutes.unsubscribe.link} />
                    <Route>
                        <Layout notice={notice}>
                            <Suspense fallback={<Loader />}>
                                <Switch>
                                    <Route component={Preview} path={AccountRoutes.preview.link} />
                                    <Route component={Account} path={AccountRoutes.account.link} />
                                    <Route component={NotFoundPage} />
                                </Switch>
                            </Suspense>
                        </Layout>
                    </Route>
                </Switch>
            </Suspense>
            {ttDev && <DevIndicator />}
        </ErrorBoundary>
    );
};
